"use client";
import React, { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import GoogleSlotPicker from "@/components/GoogleSlotPicker";

const MEETING_TIMEZONE = "Asia/Karachi";

const formatMeetingTime = (iso) =>
    new Date(iso).toLocaleString(undefined, {
        timeZone: MEETING_TIMEZONE,
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

// ── Renamed from ReschedulePage. useSearchParams() requires this
// component to be wrapped in a <Suspense> boundary (Next.js fails the
// build otherwise, since this can bail out to client-side rendering).
// All original logic below is unchanged. ──
function ReschedulePageContent() {
    const searchParams = useSearchParams();
    const submissionId = searchParams.get("submissionId");

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [rebooked, setRebooked] = useState(null); // { startTime, endTime }

    useEffect(() => {
        if (!submissionId) {
            setError("This link is missing some information. Please use the link from your confirmation email.");
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError("");

        fetch(`/api/google/booking-info?submissionId=${encodeURIComponent(submissionId)}`)
            .then((r) => r.json())
            .then((data) => {
                if (cancelled) return;
                if (!data.success) {
                    setError(data.message || "Booking not found.");
                } else if (data.meetingCancelled) {
                    setError("This meeting was already cancelled. Please book a new demo from our site.");
                } else {
                    setInfo(data);
                }
            })
            .catch(() => {
                if (!cancelled) setError("Something went wrong. Please try again.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [submissionId]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    bgcolor: "#F5F5F7",
                }}
            >
                <CircularProgress sx={{ color: "#7C3AED" }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    px: 2,
                    bgcolor: "#F5F5F7",
                }}
            >
                <Box
                    sx={{
                        bgcolor: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        p: { xs: 4, sm: 5 },
                        maxWidth: 480,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "var(--font-plusjakarta), sans-serif",
                            fontWeight: 700,
                            fontSize: "1.25rem",
                            color: "#111827",
                            mb: 1.5,
                        }}
                    >
                        Something&apos;s not right
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "var(--font-outfit), sans-serif",
                            color: "#4B5563",
                            fontSize: "0.95rem",
                            lineHeight: 1.6,
                        }}
                    >
                        {error}
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (rebooked) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    px: 2,
                    bgcolor: "#F5F5F7",
                }}
            >
                <Box
                    sx={{
                        bgcolor: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        p: { xs: 4, sm: 5 },
                        maxWidth: 480,
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: 90,
                            height: 90,
                            borderRadius: "50%",
                            backgroundColor: "#EDE4FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 3,
                            mx: "auto",
                        }}
                    >
                        <Box
                            component="svg"
                            viewBox="0 0 24 24"
                            sx={{
                                width: 46,
                                height: 46,
                                strokeWidth: 3,
                                stroke: "#FFB200",
                                fill: "none",
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                            }}
                        >
                            <path d="M20 6L9 17l-5-5" />
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: "var(--font-plusjakarta), sans-serif",
                            fontWeight: 700,
                            fontSize: { xs: "1.2rem", sm: "1.4rem" },
                            color: "#111827",
                            mb: 1.5,
                        }}
                    >
                        Rescheduled!
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "var(--font-outfit), sans-serif",
                            fontSize: "1rem",
                            color: "#111827",
                            fontWeight: 600,
                        }}
                    >
                        {formatMeetingTime(rebooked.startTime)}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "var(--font-outfit), sans-serif",
                            fontSize: "0.9rem",
                            color: "#6B7280",
                            mt: 1.5,
                        }}
                    >
                        You&apos;ll receive a new confirmation email shortly.
                    </Typography>
                   
                </Box>
            </Box>
        );
    }

    const oldTimeLabel = info?.meetingStartTime ? formatMeetingTime(info.meetingStartTime) : null;

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#F5F5F7", py: { xs: 3, md: 5 }, px: { xs: 1.5, md: 3 } }}>
            <Box sx={{ maxWidth: 1000, mx: "auto", mb: { xs: 2, md: 3 } }}>
                <Typography
                    sx={{
                        fontFamily: "var(--font-plusjakarta), sans-serif",
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", md: "1.35rem" },
                        color: "#111827",
                    }}
                >
                    Reschedule your meeting
                </Typography>
                {oldTimeLabel && (
                    <Typography
                        sx={{
                            fontFamily: "var(--font-outfit), sans-serif",
                            color: "#6B7280",
                            mt: 0.5,
                            fontSize: { xs: "0.85rem", md: "0.95rem" },
                        }}
                    >
                        Currently booked for <strong style={{ color: "#111827" }}>{oldTimeLabel}</strong> — pick a new slot below.
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    maxWidth: 1000,
                    mx: "auto",
                    bgcolor: "#fff",
                    borderRadius: { xs: "12px", md: "16px" },
                    overflow: "hidden",
                    boxShadow: "0 12px 32px rgba(16,24,40,0.10)",
                    height: { xs: "auto", md: "80vh" },
                }}
            >
                <GoogleSlotPicker
                    recruiterEmail={info.recruiterEmail}
                    candidateName={info.candidateName}
                    candidateEmail={info.candidateEmail}
                    submissionId={submissionId}
                    recruiterName="Abdul Hannan"
                    recruiterAvatarUrl="/path/to/avatar.jpg"
                    eventTitle="Stop Losing A-Players to Slow Recruitment Processes"
                    eventDescription={`How to make offers in 7 days (not 7 weeks)\nThe exact Getgeek features that'll 10x your hiring speed\nThis call is for recruiters and hiring managers who are tired of losing great candidates to faster companies.\nIf that's you, grab a slot below.`}
                    durationMinutes={30}
                    meetingProvider="Google Meet"
                    timezoneLabel={MEETING_TIMEZONE}
                    onBooked={({ startTime, endTime }) => setRebooked({ startTime, endTime })}
                />
            </Box>
        </Box>
    );
}

// ── New default export. Wraps ReschedulePageContent in a Suspense
// boundary so `next build` no longer throws the
// "useSearchParams() should be wrapped in a suspense boundary" error. ──
export default function ReschedulePage() {
    return (
        <Suspense
            fallback={
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                        bgcolor: "#F5F5F7",
                    }}
                >
                    <CircularProgress sx={{ color: "#7C3AED" }} />
                </Box>
            }
        >
            <ReschedulePageContent />
        </Suspense>
    );
}